import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, Plus, Copy, Users, AlertTriangle, ArrowRight, Loader2, Crown, LogIn, Trash2 } from "lucide-react";

const Teacher = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", level: "GCSE" });
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [overview, setOverview] = useState(null);

  const load = async () => {
    const { data } = await api.get("/classes");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const createClass = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/classes", form);
      toast.success("Class created");
      setForm({ name: "", subject: "", level: form.level });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/classes/join", { code: joinCode });
      toast.success(`Joined ${data.name}`);
      setJoinCode("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openClass = async (id) => {
    if (openId === id) { setOpenId(null); setOverview(null); return; }
    setOpenId(id); setOverview(null);
    try {
      const { data } = await api.get(`/classes/${id}/overview`);
      setOverview(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load class"); }
  };

  const removeClass = async (id) => {
    await api.delete(`/classes/${id}`);
    toast.message("Class deleted");
    setOpenId(null); setOverview(null);
    load();
  };

  if (!data) return <div className="p-10 text-neutral-500 text-[0.86rem]">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="teacher-page">
      <header className="mb-10">
        <div className="label-mono mb-3">/ classes</div>
        <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">See who&apos;s actually keeping up.</h1>
        <p className="text-neutral-500 mt-3 text-[0.92rem] max-w-2xl">
          Give students a join code. Their lessons, recall streaks, practice scores and spec coverage roll up
          into one view — so you know who needs help before the mock, not after.
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {/* teacher: create */}
        <div className="glass p-6">
          <div className="label-mono mb-4 flex items-center gap-2">
            <GraduationCap size={11}/> create a class
            <span className="ml-auto text-[9px] label-mono text-rose-400">PRO</span>
          </div>
          {data.can_create ? (
            <form onSubmit={createClass} className="space-y-2" data-testid="class-form">
              <input className="input-cyber" placeholder="Class name (e.g. 11B Physics)" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required data-testid="class-name"/>
              <input className="input-cyber" placeholder="Subject (optional)" value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} data-testid="class-subject"/>
              <select className="input-cyber" value={form.level} onChange={(e)=>setForm({...form, level:e.target.value})} data-testid="class-level">
                {["GCSE", "IGCSE", "AS-Level", "A-Level", "IB Diploma"].map((l) => <option key={l} value={l} className="bg-black">{l}</option>)}
              </select>
              <button className="btn-primary w-full" disabled={busy} data-testid="class-create">
                {busy ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Create class
              </button>
            </form>
          ) : (
            <div className="border border-dashed border-white/[0.10] p-5 rounded-[2px] text-center" data-testid="teacher-upgrade-panel">
              <Crown size={18} className="text-rose-400 mx-auto mb-3"/>
              <div className="text-[0.86rem] text-neutral-300 mb-2">Teacher dashboards are a Pro feature.</div>
              <p className="text-[0.76rem] text-neutral-500 mb-4 leading-relaxed">
                Track a whole class&apos; recall streaks, practice scores and specification coverage in one table.
              </p>
              <Link to="/pricing"><button className="btn-primary text-[0.8rem] py-2 px-3" data-testid="teacher-upgrade">Upgrade to Pro <ArrowRight size={12}/></button></Link>
            </div>
          )}
        </div>

        {/* student: join */}
        <div className="glass p-6">
          <div className="label-mono mb-4 flex items-center gap-2"><LogIn size={11}/> join a class</div>
          <form onSubmit={join} className="flex gap-2 mb-5" data-testid="join-form">
            <input
              className="input-cyber font-mono uppercase tracking-[0.2em]"
              placeholder="CODE"
              maxLength={6}
              value={joinCode}
              onChange={(e)=>setJoinCode(e.target.value.toUpperCase())}
              required
              data-testid="join-code"
            />
            <button className="btn-primary px-4" disabled={busy} data-testid="join-submit">Join</button>
          </form>
          <div className="label-mono mb-2">/ enrolled</div>
          <div className="space-y-1.5">
            {data.enrolled.length === 0 && <div className="text-neutral-500 text-[0.82rem]">You&apos;re not in any class.</div>}
            {data.enrolled.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`enrolled-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-[0.86rem] text-white truncate">{c.name}</div>
                  <div className="text-[0.7rem] text-neutral-500">taught by @{c.owner_username}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="space-y-3">
        <div className="label-mono mb-2">/ teaching</div>
        {data.teaching.length === 0 && (
          <div className="text-neutral-500 text-[0.86rem] border border-dashed border-white/[0.08] p-6 text-center rounded-[2px]">
            No classes yet.
          </div>
        )}
        {data.teaching.map((c) => (
          <div key={c.id} className="glass" data-testid={`class-${c.id}`}>
            <div className="p-6 flex items-center gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="label-mono mb-2">{c.subject || "all subjects"} {c.level ? `· ${c.level}` : ""}</div>
                <div className="font-display text-xl tightest font-semibold truncate">{c.name}</div>
                <div className="text-[0.75rem] text-neutral-500 mt-1.5 flex items-center gap-1.5">
                  <Users size={11}/> {c.student_count} student{c.student_count === 1 ? "" : "s"}
                </div>
              </div>
              <button
                onClick={() => { navigator.clipboard?.writeText(c.join_code); toast.success(`Code ${c.join_code} copied`); }}
                className="card-panel px-4 py-2.5 hover:border-white/20 transition-colors text-left"
                data-testid={`class-code-${c.id}`}
              >
                <div className="label-mono text-[0.6rem] mb-1 flex items-center gap-1.5"><Copy size={10}/> join code</div>
                <div className="font-mono text-lg tracking-[0.2em] text-white">{c.join_code}</div>
              </button>
              <button onClick={() => openClass(c.id)} className="btn-primary text-[0.8rem] py-2 px-3" data-testid={`class-open-${c.id}`}>
                {openId === c.id ? "Hide" : "Open dashboard"}
              </button>
              <button onClick={() => removeClass(c.id)} className="btn-ghost py-2 px-2" data-testid={`class-del-${c.id}`}><Trash2 size={13}/></button>
            </div>

            {openId === c.id && (
              <div className="border-t border-white/[0.06] p-6" data-testid="class-overview">
                {!overview && <div className="text-neutral-500 text-[0.84rem] flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> crunching class data…</div>}
                {overview && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <Stat label="students" value={overview.student_count} />
                      <Stat label="class avg" value={overview.class_avg_pct !== null ? `${overview.class_avg_pct}%` : "—"} />
                      <Stat label="spec covered" value={overview.class_avg_coverage !== null ? `${overview.class_avg_coverage}%` : "—"} />
                      <Stat label="inactive 7d" value={overview.inactive_7d} danger={overview.inactive_7d > 0} />
                    </div>

                    {overview.at_risk?.length > 0 && (
                      <div className="border border-amber-500/25 bg-amber-500/[0.04] rounded-[2px] p-4 mb-6" data-testid="at-risk">
                        <div className="label-mono mb-3 flex items-center gap-2 text-amber-400"><AlertTriangle size={11}/> needs re-teaching</div>
                        <div className="space-y-1.5">
                          {overview.at_risk.map((t) => (
                            <div key={t.subject} className="text-[0.84rem] text-neutral-300 flex items-center justify-between gap-3">
                              <span>{t.subject}</span>
                              <span className="text-[0.75rem] text-neutral-500 tabular">{t.students} student{t.students === 1 ? "" : "s"} · {t.avg_retention}% recall</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <table className="w-full text-[0.82rem]" data-testid="student-table">
                        <thead>
                          <tr className="text-left label-mono text-[0.6rem] border-b border-white/[0.06]">
                            <th className="py-2 pr-3">student</th>
                            <th className="py-2 pr-3">grade</th>
                            <th className="py-2 pr-3">tests</th>
                            <th className="py-2 pr-3">recall</th>
                            <th className="py-2 pr-3">spec</th>
                            <th className="py-2 pr-3">lessons</th>
                            <th className="py-2 pr-3">streak</th>
                            <th className="py-2">7d reviews</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overview.students.length === 0 && (
                            <tr><td colSpan={8} className="py-4 text-neutral-500">No students yet — share the join code.</td></tr>
                          )}
                          {overview.students.map((s) => (
                            <tr key={s.id} className="border-b border-white/[0.04]" data-testid={`student-${s.username}`}>
                              <td className="py-2.5 pr-3">
                                <div className="text-white">{s.display_name}</div>
                                <div className="text-[0.7rem] text-neutral-500">@{s.username}</div>
                              </td>
                              <td className="py-2.5 pr-3 font-display text-base">{s.predicted_grade || "—"}</td>
                              <td className="py-2.5 pr-3 tabular text-neutral-300">{s.score_pct !== null ? `${s.score_pct}%` : "—"}</td>
                              <td className={`py-2.5 pr-3 tabular ${s.retention_pct !== null && s.retention_pct < 75 ? "text-rose-400" : "text-neutral-300"}`}>
                                {s.retention_pct !== null ? `${s.retention_pct}%` : "—"}
                              </td>
                              <td className="py-2.5 pr-3 tabular text-neutral-300">{s.coverage_pct !== null ? `${s.coverage_pct}%` : "—"}</td>
                              <td className="py-2.5 pr-3 tabular text-neutral-400">{s.lessons}</td>
                              <td className="py-2.5 pr-3 tabular text-neutral-400">{s.streak}d</td>
                              <td className={`py-2.5 tabular ${s.reviews_7d === 0 ? "text-rose-400" : "text-neutral-400"}`}>{s.reviews_7d}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
};

const Stat = ({ label, value, danger }) => (
  <div className="card-panel px-4 py-3">
    <div className="label-mono text-[0.6rem] mb-1">{label}</div>
    <div className={`font-display text-2xl tightest font-semibold tabular leading-none ${danger ? "text-rose-400" : ""}`}>{value}</div>
  </div>
);

export default Teacher;
